/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';
import { useAsyncEffect } from 'src/core/hooks/useAsyncEffect';
import { Chart } from 'src/core/types/chart';

const defaultGridOptions: c3.GridOptions = { y: { show: true, lines: [] } };
const defaultZoomOptions: c3.ZoomOptions = { enabled: false };
const defaultColorOptions = { pattern: ['#004E8C', '#767676', '#83BEEC', '#ffB900', '#750B1C', '#0078D7'] };
const defaultLegendOptions: c3.LegendOptions = { show: false };

/**
 * Hook to maintain c3 charts (generation, update).
 *
 * @param chart The chart object to be generated.
 */
export const useChart = (chart: Chart) => {
    const { configurations, dataToFocus, containerElementRef } = chart;
    const generatedChart = React.useRef<c3.ChartAPI>(null);

    useAsyncEffect(async lifetime => {
        const c3 = await import('c3');
        // eslint-disable-next-line global-require
        require('c3/c3.min.css');

        const chartConfiguration: c3.ChartConfiguration = {
            ...configurations,
            grid: configurations.grid || defaultGridOptions,
            zoom: configurations.zoom || defaultZoomOptions,
            color: configurations.color || defaultColorOptions,
            legend: configurations.legend || defaultLegendOptions,
            onresize: () =>
                generatedChart.current?.resize({
                    width: containerElementRef.current?.offsetWidth,
                    height: containerElementRef.current?.offsetHeight
                })
        };
        generatedChart.current = c3.generate({ ...chartConfiguration, bindto: containerElementRef.current });
        containerElementRef.current?.setAttribute('aria-hidden', 'true');
        // we need this timeout to make sure that the page already initialized
        // So the container element size would be correctly determined.
        setTimeout(
            () =>
                generatedChart.current?.resize({
                    width: containerElementRef.current?.offsetWidth,
                    height: containerElementRef.current?.offsetHeight
                }),
            500
        );

        lifetime.add(() => generatedChart.current?.destroy());
    }, []);

    React.useEffect(() => {
        if (configurations.data.type === 'line') {
            generatedChart.current?.load({ columns: configurations.data.columns, unload: true });
        } else {
            generatedChart.current?.load({ columns: configurations.data.columns });
        }
    }, [configurations.data.columns, configurations.data.colors]);

    React.useEffect(() => {
        if (dataToFocus && dataToFocus.length > 0) {
            generatedChart.current?.focus(dataToFocus);
        } else {
            generatedChart.current?.revert();
        }
    }, [dataToFocus]);
};
